import React, { useState, useEffect, createContext, useContext, useCallback, useMemo } from 'react';
import { HTTP_STATUS, ERROR_TYPES } from '../../utils/apiResponseHandler';
import './NotificationSystem.css';

// Notification Context
const NotificationContext = createContext();

// Notification Types
const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  LOADING: 'loading'
};

// Notification Component
const Notification = ({ notification, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleRemove = React.useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(notification.id);
    }, 300);
  }, [onRemove, notification.id]);

  useEffect(() => {
    if (notification.autoClose && notification.duration > 0) {
      const timer = setTimeout(() => {
        handleRemove();
      }, notification.duration);
      return () => clearTimeout(timer);
    }
  }, [notification.autoClose, notification.duration, handleRemove]);

  const getIcon = () => {
    switch (notification.type) {
      case NOTIFICATION_TYPES.SUCCESS:
        return '✅';
      case NOTIFICATION_TYPES.ERROR:
        return '❌';
      case NOTIFICATION_TYPES.WARNING:
        return '⚠️';
      case NOTIFICATION_TYPES.INFO:
        return 'ℹ️';
      case NOTIFICATION_TYPES.LOADING:
        return '⏳';
      default:
        return 'ℹ️';
    }
  };

  const getStatusIcon = () => {
    if (notification.statusCode) {
      switch (notification.statusCode) {
        case HTTP_STATUS.OK:
        case HTTP_STATUS.CREATED:
          return '✅';
        case HTTP_STATUS.BAD_REQUEST:
          return '⚠️';
        case HTTP_STATUS.UNAUTHORIZED:
          return '🔒';
        case HTTP_STATUS.FORBIDDEN:
          return '🚫';
        case HTTP_STATUS.NOT_FOUND:
          return '🔍';
        case HTTP_STATUS.INTERNAL_SERVER_ERROR:
          return '💥';
        default:
          return '❌';
      }
    }
    return getIcon();
  };

  return (
    <div 
      className={`notification ${notification.type} ${isVisible ? 'visible' : ''} ${isExiting ? 'exiting' : ''}`}
      onClick={notification.clickable ? handleRemove : undefined}
    >
      <div className="notification-content">
        <div className="notification-icon">
          {notification.statusCode ? getStatusIcon() : getIcon()}
        </div>
        
        <div className="notification-body">
          <div className="notification-header">
            <h4 className="notification-title">{notification.title}</h4>
            {notification.statusCode && (
              <span className="status-code">HTTP {notification.statusCode}</span>
            )}
          </div>
          
          <p className="notification-message">{notification.message}</p>
          
          {notification.details && (
            <details className="notification-details">
              <summary>Technical Details</summary>
              <pre>{JSON.stringify(notification.details, null, 2)}</pre>
            </details>
          )}
          
          {notification.actions && notification.actions.length > 0 && (
            <div className="notification-actions">
              {notification.actions.map((action, index) => (
                <button
                  key={index}
                  className={`action-btn ${action.type || 'secondary'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.handler();
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        
        {notification.dismissible !== false && (
          <button 
            className="notification-close"
            onClick={(e) => {
              e.stopPropagation();
              handleRemove();
            }}
            aria-label="Close notification"
          >
            ×
          </button>
        )}
      </div>
      
      {notification.progress && (
        <div className="notification-progress">
          <div 
            className="progress-bar"
            style={{ width: `${notification.progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

// Notification Container
const NotificationContainer = () => {
  const { notifications, removeNotification } = useContext(NotificationContext);

  return (
    <div className="notification-container">
      {notifications.map(notification => (
        <Notification
          key={notification.id}
          notification={notification}
          onRemove={removeNotification}
        />
      ))}
    </div>
  );
};

// Notification Provider
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: NOTIFICATION_TYPES.INFO,
      title: 'Notification',
      message: '',
      duration: 5000,
      autoClose: true,
      dismissible: true,
      clickable: false,
      ...notification
    };

    setNotifications(prev => [...prev, newNotification]);
    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // API Response Notification Helpers
  const showApiResponse = useCallback((response, options = {}) => {
    const {
      successTitle = 'Success',
      errorTitle = 'Error',
      showSuccess = true,
      showError = true,
      customMessage = null
    } = options;

    if (response.success && showSuccess) {
      return addNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: successTitle,
        message: customMessage || response.message || 'Operation completed successfully',
        statusCode: response.statusCode,
        duration: 4000
      });
    } else if (!response.success && showError) {
      return addNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: errorTitle,
        message: customMessage || response.message || 'An error occurred',
        statusCode: response.statusCode,
        details: response.technicalMessage ? {
          technicalMessage: response.technicalMessage,
          errorType: response.errorType,
          timestamp: response.timestamp
        } : null,
        duration: 8000,
        actions: isRetryableError(response) ? [{
          label: 'Retry',
          type: 'primary',
          handler: () => {
            // This would need to be handled by the calling component
            // Retry requested silently
          }
        }] : null
      });
    }
  }, [addNotification]);

  const showSuccess = useCallback((message, title = 'Success', options = {}) => {
    return addNotification({
      type: NOTIFICATION_TYPES.SUCCESS,
      title,
      message,
      ...options
    });
  }, [addNotification]);

  const showError = useCallback((message, title = 'Error', options = {}) => {
    return addNotification({
      type: NOTIFICATION_TYPES.ERROR,
      title,
      message,
      duration: 8000,
      ...options
    });
  }, [addNotification]);

  const showWarning = useCallback((message, title = 'Warning', options = {}) => {
    return addNotification({
      type: NOTIFICATION_TYPES.WARNING,
      title,
      message,
      duration: 6000,
      ...options
    });
  }, [addNotification]);

  const showInfo = useCallback((message, title = 'Information', options = {}) => {
    return addNotification({
      type: NOTIFICATION_TYPES.INFO,
      title,
      message,
      duration: 5000,
      ...options
    });
  }, [addNotification]);

  const showLoading = useCallback((message, title = 'Loading', options = {}) => {
    return addNotification({
      type: NOTIFICATION_TYPES.LOADING,
      title,
      message,
      autoClose: false,
      dismissible: false,
      ...options
    });
  }, [addNotification]);

  const updateNotification = useCallback((id, updates) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id
          ? { ...notification, ...updates }
          : notification
      )
    );
  }, []);

  const value = useMemo(() => ({
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showApiResponse,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    updateNotification
  }), [
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showApiResponse,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    updateNotification
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
};

// Hook to use notifications
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Helper function to check if error is retryable (imported from apiResponseHandler)
const isRetryableError = (response) => {
  if (!response || response.success) return false;
  
  const retryableStatusCodes = [
    HTTP_STATUS.INTERNAL_SERVER_ERROR,
    HTTP_STATUS.BAD_GATEWAY,
    HTTP_STATUS.SERVICE_UNAVAILABLE,
    HTTP_STATUS.GATEWAY_TIMEOUT
  ];
  
  const retryableErrorTypes = [
    ERROR_TYPES.NETWORK,
    ERROR_TYPES.TIMEOUT,
    ERROR_TYPES.SERVER
  ];
  
  return retryableStatusCodes.includes(response.statusCode) || 
         retryableErrorTypes.includes(response.errorType);
};

export default NotificationProvider;
